import { useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';

//hooks
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';

//assets
import ShipperGuideLogo from '@/public/images/home/logos/shipperguide.svg';
import OpendockLogo from '@/public/images/home/logos/opendock.svg';
import LoadsmartLogo from '@/public/images/logos/loadsmart_logotype.svg';

//components
import {
  Section,
  Kicker,
  KickerDot,
  SectionTitle,
  SectionLede,
  Grad,
} from '@/containers-ai/SectionHeading';

import { PlatformScale } from '@/containers-latam/PlatformScale';

//styles
import {
  RailWrap,
  Manager,
  ManagerDot,
  Rail,
  Progress,
  Stage,
  StageNode,
  StageName,
  RailCaps,
  RailCap,
  StagePanel,
  StageName2,
  StageProductTag,
  StageList,
  StageListItem,
  StageCard,
  StageCardStatus,
  StageCardTitle,
  StageCardMeta,
  StageCardValue,
} from './LifecycleRail.styles';
import { Container } from '@/components/Grid';

// Non-translatable stage metadata (logos, product names, layout flags) — merged
// at render time with the translatable fields (name, list, event) from `content.stages`,
// which is keyed by the same slugs.
const stageMeta = {
  planning: {
    product: 'ShipperGuide',
    productLogo: (
      <ShipperGuideLogo
        role="img"
        aria-label="ShipperGuide"
        width="117"
        height="14"
      />
    ),
  },
  procurement: {
    product: 'ShipperGuide',
    productLogo: (
      <ShipperGuideLogo
        role="img"
        aria-label="ShipperGuide"
        width="117"
        height="14"
      />
    ),
  },
  execution: {
    product: 'ShipperGuide',
    productLogo: (
      <ShipperGuideLogo
        role="img"
        aria-label="ShipperGuide"
        width="117"
        height="14"
      />
    ),
  },
  settlement_payments: {
    product: 'ShipperGuide',
    productLogo: (
      <ShipperGuideLogo
        role="img"
        aria-label="ShipperGuide"
        width="117"
        height="14"
      />
    ),
  },
  gate: {
    product: 'Opendock',
    productLogo: (
      <OpendockLogo role="img" aria-label="Opendock" width="89" height="13" />
    ),
    facility: true,
  },
  yard: {
    product: 'Opendock',
    productLogo: (
      <OpendockLogo role="img" aria-label="Opendock" width="89" height="13" />
    ),
    facility: true,
  },
  dock: {
    product: 'Opendock',
    productLogo: (
      <OpendockLogo role="img" aria-label="Opendock" width="89" height="13" />
    ),
    facility: true,
  },
};

const AUTO_ADVANCE_MS = 3400;
const RESUME_AFTER_MS = 12000;

const CAP_LOGOS = {
  shipperguide: (
    <ShipperGuideLogo
      role="img"
      aria-label="ShipperGuide"
      width="117"
      height="14"
    />
  ),
  opendock: (
    <OpendockLogo role="img" aria-label="Opendock" width="89" height="13" />
  ),
  loadsmart: (
    <LoadsmartLogo role="img" aria-label="Loadsmart" width="92" height="15" />
  ),
};

// Default (US) links under the rail; pages can pass their own `caps`.
const defaultCaps = (railCaps) => [
  {
    logo: 'shipperguide',
    text: railCaps.shipperguideSuffix,
    href: 'https://shipperguide.com/?utm_source=loadsmart-website',
    className: 'sg qlf-btn-5',
  },
  {
    logo: 'opendock',
    text: railCaps.opendockSuffix,
    href: 'https://opendock.com/?utm_source=loadsmart-website',
    className: 'od qlf-btn-6',
  },
];

// `fixedStage` (a stage slug) pins the panel to that stage: no auto-advance and
// the stage nodes stop being clickable. Used when only one stage has copy.
export function LifecycleRail({
  content,
  platformScaleContent,
  showPlatformScale = true,
  fixedStage,
  caps,
  showProductTag = true,
}) {
  const stages = Object.keys(stageMeta).map((slug) => ({
    ...stageMeta[slug],
    ...content.stages[slug],
  }));
  const prefersReducedMotion = usePrefersReducedMotion();
  const [activeIndex, setActiveIndex] = useState(
    fixedStage ? Math.max(0, Object.keys(stageMeta).indexOf(fixedStage)) : 0
  );
  const { ref: railRef, inView: railInView } = useInView({
    threshold: 0.35,
    triggerOnce: true,
  });
  const autoTimerRef = useRef(null);
  const resumeTimerRef = useRef(null);

  useEffect(() => {
    if (fixedStage || prefersReducedMotion || !railInView) return undefined;
    autoTimerRef.current = setInterval(() => {
      setActiveIndex((current) => (current + 1) % stages.length);
    }, AUTO_ADVANCE_MS);
    return () => clearInterval(autoTimerRef.current);
  }, [prefersReducedMotion, railInView]);

  useEffect(
    () => () => {
      clearInterval(autoTimerRef.current);
      clearTimeout(resumeTimerRef.current);
    },
    []
  );

  function handleStageClick(index) {
    if (fixedStage) return;
    setActiveIndex(index);
    clearInterval(autoTimerRef.current);
    clearTimeout(resumeTimerRef.current);

    if (prefersReducedMotion) return;

    resumeTimerRef.current = setTimeout(() => {
      autoTimerRef.current = setInterval(() => {
        setActiveIndex((current) => (current + 1) % stages.length);
      }, AUTO_ADVANCE_MS);
    }, RESUME_AFTER_MS);
  }

  const activeStage = stages[activeIndex];
  const progressWidth = ((activeIndex + 0.5) / stages.length) * 100 - 3;

  return (
    <Section id="platform">
      <Container>
        <Kicker>
          <KickerDot />
          {content.kicker}
        </Kicker>
        <SectionTitle>
          {content.title}
          {content.titleHighlight && (
            <>
              <br />
              <Grad>{content.titleHighlight}</Grad>
            </>
          )}
        </SectionTitle>
        <SectionLede>{content.lede}</SectionLede>

        <RailWrap ref={railRef}>
          <Manager>
            <ManagerDot />
            {content.managerLabel}
          </Manager>

          <Rail>
            <Progress $width={progressWidth} />
            {stages.map((stage, index) => (
              <Stage
                key={stage.name}
                type="button"
                $static={!!fixedStage}
                tabIndex={fixedStage ? -1 : undefined}
                onClick={() => handleStageClick(index)}
              >
                <StageNode
                  $active={index === activeIndex}
                  $facility={stage.facility}
                />
                <StageName $active={index === activeIndex}>
                  {stage.name}
                </StageName>
              </Stage>
            ))}
          </Rail>

          <RailCaps>
            {(caps ?? defaultCaps(content.railCaps)).map((cap) => (
              <RailCap key={cap.href} href={cap.href} className={cap.className}>
                <b>{CAP_LOGOS[cap.logo]}</b> &mdash; {cap.text}{' '}
                <span className="arw">&#8594;</span>
              </RailCap>
            ))}
          </RailCaps>

          <StagePanel>
            <div>
              <StageName2>
                {activeStage.name}
                {showProductTag && (
                  <StageProductTag>{activeStage.productLogo}</StageProductTag>
                )}
              </StageName2>
              <StageList>
                {activeStage.list.map((item) => (
                  <StageListItem key={item}>{item}</StageListItem>
                ))}
              </StageList>
            </div>
            <StageCard>
              <StageCardStatus>{content.stageStatusLabel}</StageCardStatus>
              <StageCardTitle>{activeStage.event.title}</StageCardTitle>
              <StageCardMeta>
                <b>{activeStage.event.agent}</b> &middot;{' '}
                {activeStage.event.ref}
              </StageCardMeta>
              {activeStage.event.value && (
                <StageCardValue>{activeStage.event.value}</StageCardValue>
              )}
            </StageCard>
          </StagePanel>
        </RailWrap>
        {showPlatformScale && (
          <PlatformScale
            stats={Object.values(platformScaleContent.stats)}
            label={platformScaleContent.label}
            locale={platformScaleContent.locale}
          />
        )}
      </Container>
    </Section>
  );
}
